import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Slider, Slide, CarouselContext } from 'pure-react-carousel';

import Image from '~/components/base/Image';

import phoneScreen from '~/images/phone.png';
import radialPlusApp from '~/images/singleIllustrations/radialPlusApp.png';
import appShapes from '~/images/singleIllustrations/appShapes.png';
import { kebabCase } from 'lodash';
import { calcVw } from '~/styles/helpers';

const StyledSlider = styled(Slider)`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .carousel__slide {
        opacity: 1;
        transition: opacity 1s ease-out;
    }

    .carousel__slide--hidden {
        opacity: 0;
    }
`;

const StyledPhoneSlider = styled(StyledSlider)`
    height: 100%;

    .carousel__slider,
    .carousel__slider-tray-wrapper,
    .carousel__slider-tray {
        height: 100%;
        width: 100%;
    }
`;

const SliderWrapper = styled.div`
    height: 100vh;
    width: 100%;
    position: relative;
    ${({ theme, usePhoneScreen, singleSlide }) =>
        usePhoneScreen &&
        !singleSlide &&
        `
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) skewY(-1.25deg);
    transform-origin: 0 0;
    width: calc(50% + ${theme.layout.gutter});
    height: 100%;
    max-height: 495px;
    box-shadow: inset  0 -40px 75px -40px rgba(0, 0, 0, 0.75), 100px 30px 30px -40px rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(100px);

    @media (min-width: ${theme.breakpoint.largeTablet}) {
      max-height: 572px;
    }

    @media (min-width: ${theme.breakpoint.desktop}) {
      max-height: 700px;
      width: calc(50% + (${theme.layout.gutter} * 2));
    }

    @media(min-width: ${theme.layout.containerWidth}) {
      width: calc(50% + (${theme.layout.gutter} * 2) + (${calcVw(50)} - ${theme.layout.containerWidthNumber / 2}px));
    }

    @media(min-width: ${theme.breakpoint.largeDesktop}) {
        width: calc(50% + (${theme.layout.gutter} * 2) + (${calcVw(50)} - ${theme.layout.containerWidthLgNumber / 2}px));
    }
  }
  `};

    @media (min-height: 965px) {
        transform: translateY(-120px);
    }
`;

const PhoneScreen = styled(Image)`
    position: absolute;
    width: 100%;
    height: 100%;
    max-height: 495px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        max-height: 572px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        max-height: 700px;
    }
`;

const StyledImage = styled(Image)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
`;

const PhoneImageWrapper = styled.div`
    position: absolute;
    height: calc(100% - 20px);
    aspect-ratio: 233 / 495;
    max-height: 475px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    overflow: hidden;
    border-radius: 33px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        max-height: 552px;
        border-radius: 45px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        max-height: 675px;
    }
`;

const StyledSlide = styled(Slide)`
    height: 100%;
    width: 100% !important;
`;

const PhoneShapesTop = styled(Image)`
    position: absolute;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        top: 90px;
        width: 165px;
        right: -20px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        width: 225px;
        top: 150px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        top: 0;
        width: 200px;
    }

    @media (min-width: ${({ theme }) => theme.layout.containerWidth}) {
        width: 225px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeDesktop}) {
        width: 500px;
        right: -140px;
    }
`;

const PhoneShapesBottom = styled(Image)`
    position: absolute;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        bottom: 90px;
        width: 165px;
        left: -20px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        width: 225px;
        bottom: 150px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        bottom: 125px;
        width: 200px;
        left: -20px;
    }

    @media (min-width: ${({ theme }) => theme.layout.containerWidth}) {
        width: 225px;
        left: -30px;
        bottom: 125px;
    }

    @media (min-width: 1814px) {
        width: 251px;
        left: -20px;
        bottom: 50px;
    }
`;

const ImageSlider = ({ slides, usePhoneScreen, featuresCarouselState }) => {
    const carouselContext = useContext(CarouselContext);

    useEffect(() => {
        carouselContext.setStoreState({
            currentSlide: featuresCarouselState.currentSlide
        });
    }, [featuresCarouselState.currentSlide, carouselContext]);

    return (
        <SliderWrapper {...{ usePhoneScreen }} singleSlide={slides.length < 2}>
            {usePhoneScreen && (
                <>
                    <PhoneShapesTop sourceUrl={radialPlusApp} />
                    <PhoneShapesBottom sourceUrl={appShapes} />
                    <PhoneScreen sourceUrl={phoneScreen} objectFit="contain" />
                </>
            )}
            {usePhoneScreen ? (
                <StyledPhoneSlider classNameAnimation="fade-animation">
                    {slides.map(({ image, title }, index) => (
                        <StyledSlide
                            key={`${image.id}--${kebabCase(title)}`}
                            {...{ index }}
                        >
                            <PhoneImageWrapper>
                                <Image {...image} objectFit="contain" />
                            </PhoneImageWrapper>
                        </StyledSlide>
                    ))}
                </StyledPhoneSlider>
            ) : (
                <StyledSlider classNameAnimation="fade-animation">
                    {slides.map(({ image, title }, index) => (
                        <StyledSlide
                            key={`${image.id}--${kebabCase(title)}`}
                            {...{ index }}
                        >
                            <StyledImage {...image} objectFit="contain" />
                        </StyledSlide>
                    ))}
                </StyledSlider>
            )}
        </SliderWrapper>
    );
};

ImageSlider.propTypes = {
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            image: PropTypes.shape({
                id: PropTypes.string.isRequired,
                altText: PropTypes.string,
                sourceUrl: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.shape({
                        gatsbyImageData: PropTypes.object.isRequired
                    })
                })
            }).isRequired
        })
    ),
    usePhoneScreen: PropTypes.bool,
    featuresCarouselState: PropTypes.object.isRequired,
    featuresCarouselDispatch: PropTypes.func.isRequired
};

export default ImageSlider;
